<script>
import { mapGetters } from 'vuex';

import UnitLog from './UnitLog.vue';
import RawDataDisplay from './RawDataDisplay.vue';
import UnitLastData from './UnitLastData.vue';
import UnitAdministration from './UnitAdministration/UnitAdministration.vue';
import Configuration from './Configuration.vue';
import SensorDetails from './SensorDetails.vue';
import UnitPosition from './UnitPosition.vue';

export default {
    name: 'UnitDataManager',

    props: {
        unit: Object,
    },

    data() {
        return {
            // Whether or not to snap the status blocks open
            snapCollapsibles: false,
            // Save widget bookkeeping
            edits: false,
            saveInProgress: false,
            saveFailed: false,
            saveSucceeded: false,
        };
    },

    computed: {
        ...mapGetters(['isUserAdmin', 'isOrganizationAdmin']),

        armState() {
            return this.lastCheckin?.arm_state ?? '';
        },

        extCheckinSec() {
            return this.unit?.ext_checkin_sec ?? 0;
        },

        lastCheckin() {
            return this.unit?.last_checkin?.message?.low ?? {};
        },

        lastCommand() {
            return this.unit?.last_command ?? {};
        },

        lastFullCheckin() {
            return this.unit?.last_full_checkin ?? {};
        },

        mainBoardConfig() {
            return this.lastFullCheckin.message.high.main_board_config ?? {};
        },

        mainBoardData() {
            return this.lastFullCheckin?.message?.high?.main_board_data ?? {};
        },

        normCheckinSec() {
            return this.unit?.norm_checkin_sec ?? 0;
        },

        pendingArmState() {
            return this.unit?.pending_arm_state ?? '';
        },

        pendingPowerMode() {
            return this.unit?.pending_power_mode ?? '';
        },

        powerMode() {
            return this.lastCheckin?.power_mode ?? '';
        },

        saveState() {
            if (this.saveInProgress) {
                return 'sending';
            } else if (this.saveFailed) {
                return 'retry';
            } else if (this.saveSucceeded && !this.edits) {
                return 'success';
            } else if (this.edits) {
                return 'changes';
            }

            return 'hidden';
        },

        sensors() {
            return this.mainBoardData?.zwave_sensors ?? [];
        },

        sensorConfigs() {
            return this.mainBoardConfig?.zwave_sensor_configs ?? [];
        },

        shorePowerLatch() {
            return this.unit?.shore_power_latch ?? false;
        },

        unitName() {
            return this.unit?.name ?? '';
        },
    },

    watch: {
        'unit.last_full_checkin.message.high': {
            immediate: true,
            deep: true,
            handler(val) {
                // Make sure that edits is initialized with the current values if not already done
                if (!this.modemBoardEdits) {
                    if (val && val.modem_board_config) {
                        this.modemBoardEdits = JSON.parse(
                            JSON.stringify(val.modem_board_config),
                        );
                    } else {
                        this.modemBoardEdits = {};
                    }
                }

                if (!this.mainBoardEdits) {
                    if (val && val.main_board_config) {
                        this.mainBoardEdits = JSON.parse(
                            JSON.stringify(val.main_board_config),
                        );
                    } else {
                        this.mainBoardEdits = {};
                    }
                }
            },
        },
    },

    created: function () {
        // Figure out how big the unit status card is supposed to be from screen width
        window.addEventListener('resize', this.checkCollapsibleSnap);
        this.checkCollapsibleSnap();
    },

    beforeDestroy() {
        window.removeEventListener('resize', this.checkCollapsibleSnap);
    },

    methods: {
        checkCollapsibleSnap() {
            const width = window.innerWidth;
            if (width > 849) {
                this.snapCollapsibles = true;
            } else {
                this.snapCollapsibles = false;
            }
        },

        resetChanges() {
            window.location.reload();
        },

        revertAll() {
            this.edits = false;
            this.mainBoardEdits = JSON.parse(
                JSON.stringify(this.mainBoardConfig),
            );
            this.modemBoardEdits = JSON.parse(
                JSON.stringify(this.modemBoardConfig),
            );
        },

        async saveChanges() {
            this.saveInProgress = true;

            const saveStatus = {
                saveFailed: false,
                saveSucceeded: false,
                saveFailText: '',
            };

            // When saving, we need to run the save methods for the unit administration card and for
            //  sensor details if applicable because they are hitting different endpoints, so we call them directly
            for (const ref of Object.keys(this.$refs)) {
                const status = await this.$refs[ref].saveChanges();
                if (status.saveFailed) {
                    saveStatus.saveFailed = true;
                    saveStatus.saveSucceeded = false;
                } else if (status.saveSucceeded) {
                    saveStatus.saveSucceeded = true;
                }

                if (status.saveFailText) {
                    saveStatus.saveFailText = status.saveFailText;
                }
            }

            if (saveStatus.saveSucceeded) {
                this.edits = false;
            }

            this.saveFailed = saveStatus.saveFailed;
            this.saveSucceeded = saveStatus.saveSucceeded;
            this.saveFailText = saveStatus.saveFailText;

            this.saveInProgress = false;
        },

        updateMainConfig(value) {
            this.mainBoardEdits = value;
            this.edits = true;
        },

        updateModemConfig(value) {
            this.modemBoardEdits = value;
            this.edits = true;
        },
    },

    components: {
        UnitLastData: UnitLastData,
        UnitLog: UnitLog,
        RawDataDisplay: RawDataDisplay,
        Configuration: Configuration,
        UnitAdministration: UnitAdministration,
        SensorDetails: SensorDetails,
        UnitPosition: UnitPosition,
    },
};
</script>

<template>
    <div class="unit-details-page">
        <SaveWidget
            :state="saveState"
            @save="saveChanges"
            @retry="saveChanges"
            @cancel="resetChanges"
            @hide="saveSucceeded = false"
        />

        <!-- Unit Status Card -->
        <Card :width="snapCollapsibles ? '800px' : '400px'" color="blue">
            <template #header>{{ unitName }} {{ $t('status') }}</template>
            <template #body>
                <div class="unit-details-container">
                    <UnitLastData
                        v-if="unit"
                        :snapCollapsibles="snapCollapsibles"
                        :unit="unit"
                    >
                    </UnitLastData>
                    <div class="unit-details-card" v-else>
                        <div class="prop-block">
                            <div class="prop name">
                                {{ $t('statusLoading') }}
                            </div>
                        </div>
                    </div>
                </div>
            </template>
        </Card>
        <!-- Unit Commands -->
        <Card
            width="400px"
            color="blue"
            :maxWidth="'600px'"
            v-if="(isUserAdmin || isOrganizationAdmin) && unit"
        >
            <template #header>{{ $t('administration') }}</template>
            <template #body>
                <div class="unit-details-container">
                    <UnitAdministration
                        :unit="unit"
                        :powerMode="powerMode"
                        :snapCollapsibles="snapCollapsibles"
                        :unitState="armState"
                        :pendingArmState="pendingArmState"
                        :pendingPowerMode="pendingPowerMode"
                        :normalCheckinIntervalSeconds="normCheckinSec"
                        :fenceCheckinIntervalSeconds="extCheckinSec"
                        :shorePowerLatch="shorePowerLatch"
                        @commitEdits="edits = true"
                        ref="administration"
                    />
                </div>
            </template>
        </Card>
        <Card fullWidth :maxWidth="'800px'" color="blue">
            <template #header>{{ $t('sensor_plural') }}</template>
            <template #body>
                <div class="unit-details-container">
                    <SensorDetails
                        :unit="unit"
                        editable
                        :sensors="sensors"
                        :sensorConfigs="sensorConfigs"
                        @commitEdits="edits = true"
                        ref="sensors"
                    />
                </div>
            </template>
        </Card>
        <!-- Unit Position Display -->
        <Card width="850px" color="blue">
            <template #header>{{ $t('unitPosition') }}</template>
            <template #body>
                <div class="unit-details-container">
                    <UnitPosition v-if="unit" :unit="unit" />
                </div>
            </template>
        </Card>
        <!-- Last command metadata, admin only -->
        <Card
            width="400px"
            color="blue"
            :maxWidth="'600px'"
            v-if="isUserAdmin || isOrganizationAdmin"
        >
            <template #header>{{ $t('lastCommand') }}</template>
            <template #body>
                <div class="unit-details-container">
                    <RawDataDisplay :data="lastCommand"></RawDataDisplay>
                </div>
            </template>
        </Card>
        <!-- Unit Log technical support card, admin only -->
        <Card
            width="600px"
            color="blue"
            v-if="isUserAdmin || isOrganizationAdmin"
        >
            <template #header>Unit Log</template>
            <template #body>
                <div class="unit-details-container">
                    <UnitLog :unit="unit" />
                </div>
            </template>
        </Card>
        <!-- Last checkin metadata for tech support, admin only -->
        <Card
            width="400px"
            color="blue"
            :maxWidth="'600px'"
            v-if="isUserAdmin || isOrganizationAdmin"
        >
            <template #header>{{ $t('lastHighBandwidthCheckin') }}</template>
            <template #body>
                <div class="unit-details-container">
                    <RawDataDisplay :data="lastFullCheckin"></RawDataDisplay>
                </div>
            </template>
        </Card>
    </div>
</template>

<style lang="scss" scoped>
@use 'sass:color';
@use '../../../palette.scss' as *;

.unit-details-page {
    display: flex;
    flex-flow: row wrap;

    .unit-details-container {
        .unit-details-card {
            display: flex;
            flex-direction: column;
            width: 100%;
            color: $theme-dark-grey;

            .prop-block {
                display: flex;
                flex-flow: column;
                justify-content: space-between;
                align-items: baseline;
                width: 100%;
                padding: 5px 8px 25px;

                .prop {
                    width: 100%;
                    position: relative;

                    &.name {
                        font-weight: bold;
                    }
                }
            }
        }
    }
}
</style>
